import { useUTM } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { StickyBottomWrapper, BoxButton } from '@repo/ui';
import { useAuthContext } from '@repo/user-session';
import { useRouter } from 'next/router';
import { CSSProperties, useCallback, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { useRelayEnvironment } from 'react-relay';

import { benefitRefundPartnerQuery } from '@/__generated__/benefitRefundPartnerQuery.graphql';
import { useSectionScrollModel } from '@/components/common/context/SectionScrollModelProvider';
import s from '@/components/landing/Landing.module.scss';
import { WaitingModal } from '@/components/landing/WaitingModal';
import { checkHasHistory } from '@/components/tax-refund/common/graphql/query/checkSearchStatus';
import { partnerDiscountQuery } from '@/graphql/query/partner/benefit';
import { AUTH_PATHS, COMMON_PATHS } from '@/lib/constants/paths';
import { useRandomWaitingModal } from '@/lib/hooks/use-random-waiting-modal';
import { useUrlWithQueryString } from '@/lib/hooks/use-url-with-query-string';

export const LandingCtaButton = ({
  buttonText,
  buttonClassName,
  buttonClickEvent,
  style
}: {
  buttonText?: string;
  buttonClassName?: string;
  buttonClickEvent?: string;
  style?: CSSProperties;
}) => {
  const router = useRouter();
  const { auth } = useAuthContext();
  const { sendClickEvent } = useUserEventLogger();
  const { eventAttribute } = useSectionScrollModel();
  const { utmData } = useUTM();
  const relayEnvironment = useRelayEnvironment();
  const actionUrl = useUrlWithQueryString(auth.status === 'login' ? COMMON_PATHS.환급조회목록 : AUTH_PATHS.로그인);
  const partnerDiscount = useLazyLoadQuery<benefitRefundPartnerQuery>(partnerDiscountQuery, { utmCampaign: utmData?.utmCampaign ?? '' });
  const { waitingTime, showWaitingModal, waitingCallback, setWaitingCallback } = useRandomWaitingModal();
  const isPersonalTritx = sessionStorage.getItem('isPersonalTritx') === 'true';

  const clickCTAButton = () => {
    sendClickEvent(buttonClickEvent ?? 'landing_bottom_cta', eventAttribute);
    showWaitingModal();
  };

  const handleTritxNavigation = useCallback(async () => {
    if (auth.status === 'login') {
      const hasHistory = await checkHasHistory(relayEnvironment);
      if (hasHistory) {
        router.push(COMMON_PATHS.환급조회목록);
      } else {
        router.push({ pathname: COMMON_PATHS.간편인증_정보입력, query: { from: 'lookup' } });
      }
    } else {
      router.push({
        pathname: AUTH_PATHS.로그인,
        query: { return_url: '/redirect' }
      });
    }
  }, [auth.status, relayEnvironment, router]);

  useEffect(() => {
    setWaitingCallback(() => () => {
      if (isPersonalTritx) {
        handleTritxNavigation();
      } else {
        router.push(actionUrl);
      }
    });
  }, [actionUrl, router, setWaitingCallback, isPersonalTritx, handleTritxNavigation]);

  return (
    <>
      <div className={'z-20 w-full fixed bottom-5'}>
        <div className={'flex justify-center items-center flex-col'}>
          {partnerDiscount.refundPartner && (
            <div className={`mb-1 flex flex-col container-medium items-center ${s.cta_tooltip}`}>
              <div className={'flex items-center gap-1 rounded-full bg-white px-4 py-2 shadow-[0_4px_12px_0_rgba(0,0,0,0.12)]'}>
                <img className={'w-4.5 h-4.5'} src={'/images/illus-icons/fill-coin-yl-rg.svg'} alt={'yellow coin icon'} />
                <span className={'text-violet-600 text-14 font-medium text-center break-all'}>
                  {partnerDiscount.refundPartner?.partnerName} 고객님은 제휴 할인 {partnerDiscount.refundPartner?.discountRate}% 혜택까지!
                </span>
              </div>
            </div>
          )}
          <div className={'w-full lg:px-0'}>
            <StickyBottomWrapper bgColor={'transparent'} className={'sticky'}>
              <BoxButton style={style} size={'large'} variant={'filled-primary'} className={`w-full ${buttonClassName}`} onClick={clickCTAButton}>
                {buttonText ? buttonText : isPersonalTritx ? '환급금 무료로 진단받기' : '내 환급금 무료 조회'}
              </BoxButton>
            </StickyBottomWrapper>
          </div>
        </div>
      </div>
      {waitingTime > 0 && <WaitingModal waitingTime={waitingTime} callbackFunction={waitingCallback} />}
    </>
  );
};
